import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import {
  LegacyContent,
  LegacyGame,
  GameModeEnum,
  GameResults,
  GameScenarioEnum,
  Lesson,
  User,
} from 'lingo2-models';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  throwError,
} from 'rxjs';
import {
  buildNeedBoardGameData,
  isBoardGameDataMessage,
  LOAD_BOARD_GAME_TIMEOUT_MS,
  NEED_BOARD_GAME_POLL_MS,
  normalizeBoardGameSnapshot,
} from './board-game-data';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  private _token: string = '';
  private _host: string = '';
  private _mode: GameModeEnum = GameModeEnum.single;
  private _locale: string = '';
  private _userId: string = '';
  private _gameId: string = '';
  private _sessionId: string = '';

  private _lesson: LegacyContent = new LegacyContent();

  public myUser = new BehaviorSubject<User | any>(null);
  public myUser$ = this.myUser.asObservable();

  public author = new BehaviorSubject<User | any>(null);
  public author$ = this.author.asObservable();

  constructor(private http: HttpClient) {}

  public set token(token: string) {
    this._token = token;
  }

  public get token() {
    return this._token;
  }

  public set host(host: string) {
    this._host = host.replace('https://', '').replace('http://', '');
  }

  public get host() {
    return `${this._host}`;
  }

  public get lang_host() {
    return `${this._host}`;
  }

  public set mode(mode: GameModeEnum) {
    this._mode = mode;
  }

  public get mode() {
    return this._mode;
  }

  public set locale(locale: string) {
    this._locale = locale;
  }

  public get locale() {
    return this._locale || 'en';
  }

  public get hasLocale(): boolean {
    return !!this._locale;
  }

  public set userId(userId: string) {
    this._userId = userId;
  }

  public get userId() {
    return this._userId;
  }

  public set lesson(lesson: LegacyContent) {
    this._lesson = lesson;
  }

  public get lesson() {
    return this._lesson;
  }

  public set gameId(gameId: string) {
    this._gameId = gameId;
  }

  public get gameId() {
    return this._gameId;
  }

  public set sessionId(sessionId: string) {
    this._sessionId = sessionId;
  }

  public get sessionId() {
    return this._sessionId;
  }

  /** Снимок сессии доски: не ходим в content prepare */
  public loadBoardGameData(): Observable<LegacyGame> {
    const sessionId = this._sessionId;
    if (!sessionId) {
      return of(new LegacyGame({}));
    }
    return new Observable<LegacyGame>((subscriber) => {
      const finish = (game: LegacyGame) => {
        window.removeEventListener('message', onMessage);
        clearTimeout(timer);
        clearInterval(poll);
        subscriber.next(game);
        subscriber.complete();
      };
      const askParent = () => window.parent.postMessage(buildNeedBoardGameData(sessionId), '*');
      const onMessage = (e: MessageEvent) => {
        if (!isBoardGameDataMessage(e.data, sessionId)) return;
        // lingo2-models больше не экспортирует Game — конструктор снимка LegacyGame
        finish(new LegacyGame(normalizeBoardGameSnapshot(e.data.gameData as Partial<LegacyGame>)));
      };
      window.addEventListener('message', onMessage);
      askParent();
      // parent ещё готовит meetings — повторяем запрос, не один таймаут 8с
      const poll = setInterval(askParent, NEED_BOARD_GAME_POLL_MS);
      const timer = setTimeout(() => finish(new LegacyGame({})), LOAD_BOARD_GAME_TIMEOUT_MS);
      return () => {
        window.removeEventListener('message', onMessage);
        clearTimeout(timer);
        clearInterval(poll);
      };
    });
  }

  /**
   * Подготовить превью урока по ID урока
   *
   * @param contentId string
   * @param mode GameModeEnum
   * @param scenario GameScenarioEnum
   */
  public prepareGameByLessonId(
    contentId: string,
    mode?: GameModeEnum,
    scenario?: GameScenarioEnum
  ): Observable<LegacyGame> {
    if (!this.host || !this.token) {
      return of(new LegacyGame({}));
    }
    const url = `https://${this.host}/web/content/game/prepare/${contentId}`; // TODO ?mode=${mode}
    return this.http
      .post<LegacyGame>(
        url,
        { mode, scenario },
        {
          observe: 'response',
        }
      )
      .pipe(
        map(this.handleItemResponse)
        // catchError(this.handleError),
      );
  }

  /**
   * Подготовить превью урока по содержимому урока
   *
   * @param lesson Partial<Lesson>
   */
  public prepareGameByContent(lesson: Partial<Lesson>): Observable<LegacyGame> {
    if (!this.host || !this.token) {
      return of(new LegacyGame({}));
    }
    const url = `https://${this.host}/web/content/game/preview`;
    return this.http
      .post<LegacyGame>(
        url,
        { lesson },
        {
          observe: 'response',
        }
      )
      .pipe(
        map(this.handleItemResponse)
        // catchError(this.handleError),
      );
  }

  public getGameById(id: string): Observable<LegacyGame> {
    if (!this.host || !this.token) {
      return of(new LegacyGame({}));
    }
    const url = `https://${this.host}/web/content/game/${id}`;
    return this.http
      .get<LegacyGame>(url, {
        observe: 'response',
      })
      .pipe(
        map(this.handleItemResponse)
        // catchError(of("Content api exception")),
      );
  }

  /**
   * Обновить прогресс участника игры
   *
   * @param game_id string
   * @param results GameResults
   */
  public updateParticipant(
    game_id: string,
    results: Partial<GameResults>
  ): Observable<any> {
    if (!this.host || !this.token) {
      return of(null);
    }
    const url = `https://${this.host}/web/content/game/${game_id}/participant`;
    return this.http
      .put(url, results, {
        observe: 'response',
      })
      .pipe(
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            return throwError(() => err);
          }
          return of({ type: '[Game API] Update participant error' });
        })
      );
  }

  private handleItemResponse(response: HttpResponse<LegacyGame>): LegacyGame {
    return new LegacyGame(response.body as any);
  }

  public sendMessage(body: any) {
    window.parent.postMessage(body, '*');
  }

  public getUserById(user_id: string): Observable<User> {
    if (!user_id) {
      return of();
    }
    const url = `https://${this.host}/web/account/account/${user_id}`;
    const params = new HttpParams();
    return this.http
      .get<User>(url, {
        params,
        observe: 'response',
      })
      .pipe(map((r) => r.body!));
  }
}
